import { z } from 'zod';
/**
 * Hero image attached to a country article. Populated by the image-attach
 * pass (`scripts/seo/attach-images.py`) from `country-images.json`.
 *
 * Optional everywhere — articles render gracefully without one. ~15% of
 * countries (small islands, Cayman/Montserrat/CAR/etc.) have no Wiki Commons
 * landmark photo and ship without `hero`.
 */
const HeroImage = z.object({
    src: z.string(), // /img/countries/<slug>-hero.webp
    width: z.number(),
    height: z.number(),
    alt: z.string(),
    credit: z.string().optional(), // "Photo by X on Unsplash" / contributor
    license: z.string().optional(), // "Unsplash License" / "CC BY-SA 4.0"
    licenseUrl: z.string().optional(),
    sourceUrl: z.string().optional(), // canonical photo page (Unsplash photo URL)
    // Unsplash API ToS requires linking back to the photographer's profile with
    // utm tracking. Optional for non-Unsplash sources (Wikimedia attribution
    // doesn't require this layer).
    photographerUrl: z.string().optional(),
});
const ArticleBase = z.object({
    title: z.string(),
    description: z.string(),
    h1: z.string(),
    lede: z.string(),
    /**
     * Pre-rendered HTML. Treated as TRUSTED — content originates from our own
     * curated articles pipeline (28 .docx → JSON), never user input. If user-
     * submitted bodies are ever introduced, sanitize at parse time and add a
     * `bodySource: 'curated' | 'user'` discriminator before relaxing this.
     */
    body: z.string(),
    hero: HeroImage.optional(),
    // YYYY-MM-DD when the article body last changed in substance (bumped by
    // hand with the edit; layout or photo changes do not count). Surfaced in
    // UI as visible "Last updated:" line and in JSON-LD as `dateModified`.
    // Critical recency signal for Google AI Overviews + Perplexity +
    // ChatGPT source-card display when picking citations.
    lastUpdated: z.string().optional(),
});
export const BlogArticle = ArticleBase.extend({
    type: z.literal('article'),
    category: z.string().optional(),
    readTime: z.string().optional(),
    /**
     * Which locales this post is actually translated into. MUST mirror which
     * `blog-<loc>.json` files contain the slug (and therefore what
     * sitemap-blog.xml emits). Absent → present in all 5 locales (the common
     * case; fully-localized posts omit it). Read by BOTH hreflang emitters
     * (backend `util/hreflang.ts` + frontend `i18n/hreflang.ts`) so a
     * partially-localized post never advertises a locale it lacks — the same
     * duplicate/redirect trap we hit on the EN-only agent pages. 'en' is the
     * canonical and always implicitly present.
     */
    locales: z.array(z.string()).optional(),
    /**
     * schema.org Event payload for event-connectivity posts (events-calendar
     * initiative, 2026-07-13). When present on the EN entry, backend
     * util/json-ld.ts appends it to the page's @graph as an Event node.
     * Dates/venue are verified at write time; refresh annually.
     */
    eventJsonLd: z.record(z.unknown()).optional(),
});
/**
 * GEO #2 — per-country FAQ pairs. 4-5 PAA-shaped questions per country
 * (data-driven, factual, verifiable) that AI engines can lift directly
 * into responses. Mirrored both as visible HTML accordion and JSON-LD
 * `FAQPage` so the structured data is grounded in displayed content
 * (Google requirement for FAQ rich result eligibility).
 */
const FaqPair = z.object({
    q: z.string(),
    a: z.string(),
});
export const CountryArticle = ArticleBase.extend({
    type: z.literal('country'),
    countryName: z.string(),
    countryCode: z.string().optional(),
    flag: z.string().optional(),
    rate: z.string().optional(),
    faq: z.array(FaqPair).optional(),
});
export const Article = z.discriminatedUnion('type', [BlogArticle, CountryArticle]);
export const ArticlesMap = z.record(z.string(), Article);
